import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Music, Pause, Play, Square, X } from "lucide-react";

import MeditationIcon from "../../../../shared/ui/icons/MeditationIcon";
import {
    meditationBackgroundUrl,
    useMeditationAudioSource,
} from "../../application/useMeditationAudio";
import {
    useCenterMeditationTracks,
    useMeditationTracks,
    useRecordTrackDuration,
    trackPlaybackUrl,
} from "../../application/useMeditationTracks";
import { probeAudioDuration } from "../../domain/audioProbe";
import { BACKGROUND_TRACKS } from "../../domain/backgroundTracks";
import { BackgroundTrackId } from "../../domain/types";
import type { MeditationChannel } from "../../application/useMeditationChannel";
import { useMeditationStage } from "../../application/useMeditationStage";
import { useAvailableMeditations } from "../../application/useMeditations";
import {
    MeetingSelection,
    MeetingSelectionKind,
    meetingSelectionDuration,
    meetingSelectionScript,
    meetingSelectionState,
    meetingSelectionTitle,
} from "../../domain/meetingSelection";
import { Meditation, MeditationTrack, formatDuration } from "../../domain/types";
import MeditationPrompter from "./MeditationPrompter";

type Props = {
    channel: MeditationChannel;
    /** Put the panel away here. Never ends the meditation for both people. */
    onClose: () => void;
    /**
     * Told when a meditation is chosen or let go, so the stage can hand the
     * screen back to the video and keep the script over it rather than
     * instead of it.
     */
    onRunningChange?: (running: boolean) => void;
    /** Sounds the room's singing bowl, where a script asked for one. */
    onGong?: () => void;
    /** Calls off whatever the script set going, when it is let go. */
    onMeditationEnd?: () => void;
};

/**
 * How long the patient is told a meditation has begun.
 *
 * Long enough to read and settle, short enough that it is not a screen to sit
 * and watch.
 */
const PATIENT_NOTICE_MS = 10_000;

/**
 * A meditation inside a call.
 *
 * The two screens are deliberately not the same. The therapist gets the
 * script, the clock and the controls; the patient gets the title and a
 * progress bar and nothing else — they are supposed to have their eyes closed,
 * and a screen full of controls is an invitation to open them.
 *
 * The sound is the therapist's playback, published into the room, so both
 * people hear the same audio at the same moment rather than each streaming a
 * copy that drifts. That is also why the patient's side has no play button:
 * there is nothing local for it to control.
 */
export default function MeetingMeditation({
    channel,
    onClose,
    onRunningChange,
    onGong,
    onMeditationEnd,
}: Props) {
    const { t } = useTranslation();

    return channel.canControl ? (
        <TherapistMeditation
            channel={channel}
            onClose={onClose}
            onRunningChange={onRunningChange}
            onGong={onGong}
            onMeditationEnd={onMeditationEnd}
        />
    ) : (
        <PatientMeditation channel={channel} label={t("meditation.title")} />
    );
}

/**
 * The two shelves of the chooser, in the order a therapist reaches for them.
 *
 * Their meditations come first because that is what most sessions play. Their
 * music is one press away rather than buried, because guiding live over a
 * track is not a lesser case — it is how a good part of the work is done.
 */
const CHOOSER_TABS: readonly { id: MeetingSelectionKind; labelKey: string }[] = [
    {
        id: MeetingSelectionKind.MEDITATION,
        labelKey: "meditation.meeting.tabs.meditations",
    },
    { id: MeetingSelectionKind.TRACK, labelKey: "meditation.meeting.tabs.tracks" },
];

function TherapistMeditation({
    channel,
    onClose,
    onRunningChange,
    onGong,
    onMeditationEnd,
}: Props) {
    const { t } = useTranslation();
    // Their own and their centre's: either can be taken into a session.
    const { data: meditations, isLoading } = useAvailableMeditations();
    const stage = useMeditationStage();
    const audioSource = useMeditationAudioSource();

    const { data: personalTracks } = useMeditationTracks();
    const { data: centerTracks } = useCenterMeditationTracks();
    const recordDuration = useRecordTrackDuration();
    const ownTracks = useMemo(
        () => [...(personalTracks ?? []), ...(centerTracks ?? [])],
        [personalTracks, centerTracks]
    );

    const [selected, setSelected] = useState<MeetingSelection | null>(null);
    const [tab, setTab] = useState<MeetingSelectionKind>(
        MeetingSelectionKind.MEDITATION
    );
    const [preparing, setPreparing] = useState(false);
    const [failed, setFailed] = useState(false);
    // A recording whose file is gone: fixed by uploading it again, not by retrying.
    const [recordingMissing, setRecordingMissing] = useState(false);
    /*
     * The bed under a script the therapist is reading aloud.
     *
     * Chosen here rather than only in the editor, because a meditation written
     * once is read in different rooms: the same script wants the ocean with
     * one patient and silence with the next, and that is a decision for the
     * session it is being read in.
     */
    const [liveBed, setLiveBed] = useState<string>(BackgroundTrackId.NONE);
    // The blob URL handed to the stage, revoked when another meditation
    // replaces it rather than leaking one per selection.
    const voiceUrlRef = useRef<string | null>(null);

    const releaseVoice = useCallback(() => {
        if (voiceUrlRef.current) {
            URL.revokeObjectURL(voiceUrlRef.current);
            voiceUrlRef.current = null;
        }
    }, []);

    useEffect(() => releaseVoice, [releaseVoice]);

    useEffect(() => {
        onRunningChange?.(selected !== null);
    }, [onRunningChange, selected]);

    /** Tell the other side where we are, whenever that changes deliberately. */
    const announce = useCallback(
        (selection: MeetingSelection, playing: boolean, position: number) => {
            channel.publishState(meetingSelectionState(selection, playing, position));
        },
        [channel]
    );

    /**
     * Tell the other side whenever playback stops, however it stopped.
     *
     * The therapist pausing and the recording reaching its end look the same
     * from the patient's side and must be reported the same. Only the
     * transition is announced: after it fires the ref is false, so the
     * position ticks that follow do not each send a packet.
     */
    const wasPlaying = useRef(false);
    useEffect(() => {
        if (!selected) {
            wasPlaying.current = false;
            return;
        }
        if (wasPlaying.current && !stage.playing) {
            announce(selected, false, stage.position);
        }
        wasPlaying.current = stage.playing;
    }, [announce, selected, stage.playing, stage.position]);

    const chooseMeditation = async (meditation: Meditation) => {
        setFailed(false);
        setRecordingMissing(false);
        setPreparing(true);
        releaseVoice();
        try {
            let voiceUrl: string | null = null;
            if (meditation.has_audio) {
                voiceUrl = await audioSource.load(meditation.id);
                voiceUrlRef.current = voiceUrl;
            }
            const backgroundUrl = meditation.has_custom_background
                ? await meditationBackgroundUrl(meditation.id)
                : null;
            await stage.load({
                voiceUrl,
                backgroundTrack: meditation.background_track,
                backgroundUrl,
                // Played the way it was approved in the studio.
                voiceVolume: meditation.voice_volume,
                backgroundVolume: meditation.background_volume,
            });
            const selection: MeetingSelection = {
                kind: MeetingSelectionKind.MEDITATION,
                meditation,
            };
            setSelected(selection);
            setLiveBed(
                meditation.has_custom_background && meditation.background_track_id
                    ? meditation.background_track_id
                    : meditation.background_track
            );
            channel.startMeditation(meetingSelectionState(selection, false, 0));
        } catch (cause) {
            const status = (cause as { response?: { status?: number } })?.response
                ?.status;
            if (status === 404) setRecordingMissing(true);
            else setFailed(true);
        } finally {
            setPreparing(false);
        }
    };

    /**
     * Put a piece of the therapist's own music on, and nothing else.
     *
     * Not every meditation is written down first. A therapist who guides in
     * their own words needs a track and a play button, and asking them to
     * wrap one in a meditation with an empty script — so that the room has
     * something to accept — would be making them file paperwork to press
     * play.
     *
     * The track is loaded as the voice rather than as a bed: it is the whole
     * of what is heard, so it plays at its own level, ends when it ends
     * instead of looping under a reading, and gives the clock and the seek
     * bar a length to work from.
     */
    const chooseTrack = async (track: MeditationTrack) => {
        setFailed(false);
        setRecordingMissing(false);
        setPreparing(true);
        releaseVoice();
        try {
            const voiceUrl = await trackPlaybackUrl(track.id);
            voiceUrlRef.current = voiceUrl;
            await stage.load({
                voiceUrl,
                backgroundTrack: BackgroundTrackId.NONE,
                backgroundUrl: null,
                voiceVolume: 1,
            });
            // A track uploaded before lengths were measured has none. The file
            // is already here, so reading its length costs nothing: it gives
            // the clock and the patient's progress bar a length now, and is
            // kept so the list shows it from then on.
            let measured = track;
            if (!track.duration_seconds) {
                const seconds = await probeAudioDuration(voiceUrl);
                if (seconds > 0) {
                    measured = { ...track, duration_seconds: seconds };
                    recordDuration.mutate({ id: track.id, seconds });
                }
            }
            const selection: MeetingSelection = {
                kind: MeetingSelectionKind.TRACK,
                track: measured,
            };
            setSelected(selection);
            channel.startMeditation(meetingSelectionState(selection, false, 0));
        } catch {
            setFailed(true);
        } finally {
            setPreparing(false);
        }
    };

    const play = async () => {
        if (!selected) return;
        await stage.play();
        announce(selected, true, stage.position);
    };

    const pause = () => {
        stage.pause();
        // The other side is told by the effect below rather than here, so that
        // a recording that simply ran out is announced too — otherwise the
        // patient's progress bar keeps advancing over silence.
    };

    const end = async () => {
        await stage.close();
        releaseVoice();
        setSelected(null);
        channel.stopMeditation();
        // Anything the script set going stops with it: a bowl that arrives
        // after the meditation was ended is a bowl nobody can account for.
        onMeditationEnd?.();
    };

    /**
     * Swap the music under a script without losing where the reading is.
     *
     * Only offered for a meditation with no recording of its own: a take was
     * approved in the studio with its bed under it, and changing that here
     * would be editing somebody's finished work from the room it is played in.
     */
    const changeBed = async (choice: string) => {
        if (!selected) return;
        setLiveBed(choice);
        const own = (ownTracks ?? []).some((track) => track.id === choice);
        try {
            await stage.load({
                voiceUrl: null,
                backgroundTrack: own
                    ? BackgroundTrackId.NONE
                    : (choice as BackgroundTrackId),
                backgroundUrl: own ? await trackPlaybackUrl(choice) : null,
            });
            if (stage.playing) await stage.play();
        } catch {
            setFailed(true);
        }
    };

    const seek = (seconds: number) => {
        if (!selected) return;
        stage.seek(seconds);
        announce(selected, stage.playing, seconds);
    };

    const duration =
        stage.duration || (selected ? meetingSelectionDuration(selected) : 0);

    /*
     * A script the therapist reads aloud has nothing to press play on: what
     * the button starts is the prompter and the music, so it says so. A
     * recording — and a track played on its own — is a recording.
     */
    const carriesRecording =
        selected?.kind === MeetingSelectionKind.MEDITATION
            ? selected.meditation.has_audio
            : true;
    const readAloud =
        Boolean(selected && meetingSelectionScript(selected)) && !carriesRecording;
    const playLabel = stage.playing
        ? "meditation.meeting.pause"
        : readAloud
          ? "meditation.meeting.start"
          : "meditation.meeting.play";

    return (
        <section
            // Over the video rather than instead of it, in both states: the
            // therapist is reading to somebody, and both a script and a
            // library that hide their patient's face are the wrong trade.
            //
            // A running meditation takes the full height of the column it is
            // given, because the script is what is being read from and a
            // taller column shows more of it; the chooser is a short list and
            // keeps to the lower edge.
            className={`flex min-h-0 w-full flex-col gap-3 rounded-2xl border border-room-line/60 bg-room-raised/90 p-3 backdrop-blur-md ${
                selected ? "max-h-[45vh] sm:max-h-full" : "max-h-[50vh] max-w-md"
            }`}
            aria-label={t("meditation.title")}
        >
            <header className="flex items-start justify-between gap-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-room-ink">
                    <MeditationIcon size={16} />
                    {selected
                        ? meetingSelectionTitle(selected)
                        : t("meditation.meeting.choose")}
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t("meditation.meeting.hide")}
                    className="flex size-9 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active"
                >
                    <X size={16} />
                </button>
            </header>

            {channel.transportFailed && (
                <p role="status" className="text-xs text-room-warn">
                    {t("meditation.meeting.transportWarning")}
                </p>
            )}

            {!selected ? (
                <div className="flex min-h-0 flex-1 flex-col gap-2">
                    <div
                        role="tablist"
                        aria-label={t("meditation.meeting.choose")}
                        className="flex shrink-0 gap-1 rounded-xl bg-room-active/60 p-1"
                    >
                        {CHOOSER_TABS.map(({ id, labelKey }) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                id={`meditation-chooser-tab-${id}`}
                                aria-selected={tab === id}
                                aria-controls={`meditation-chooser-panel-${id}`}
                                onClick={() => setTab(id)}
                                className={`min-h-9 flex-1 rounded-lg px-3 text-xs font-medium ${
                                    tab === id
                                        ? "bg-room-raised text-room-ink"
                                        : "text-room-ink-muted hover:bg-room-active"
                                }`}
                            >
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>

                    <div
                        role="tabpanel"
                        id={`meditation-chooser-panel-${tab}`}
                        aria-labelledby={`meditation-chooser-tab-${tab}`}
                        className="min-h-0 flex-1 overflow-y-auto"
                    >
                        {preparing ||
                        (tab === MeetingSelectionKind.MEDITATION && isLoading) ? (
                            <p className="flex items-center gap-2 text-sm text-room-ink-muted">
                                <Loader2
                                    size={15}
                                    className="animate-spin"
                                    aria-hidden="true"
                                />
                                {t("common.loading")}
                            </p>
                        ) : tab === MeetingSelectionKind.MEDITATION ? (
                            !meditations || meditations.length === 0 ? (
                                <p className="text-sm text-room-ink-muted">
                                    {t("meditation.meeting.emptyLibrary")}
                                </p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {meditations.map((meditation) => (
                                        <li key={meditation.id}>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    void chooseMeditation(meditation)
                                                }
                                                className="flex w-full min-h-11 items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm text-room-ink hover:bg-room-active"
                                            >
                                                <span className="min-w-0">
                                                    <span className="block truncate font-medium">
                                                        {meditation.title}
                                                    </span>
                                                    <span className="block truncate text-xs text-room-ink-muted">
                                                        {t(
                                                            `meditation.kinds.${meditation.kind}`
                                                        )}
                                                    </span>
                                                </span>
                                                <span className="shrink-0 tabular-nums text-xs text-room-ink-muted">
                                                    {formatDuration(
                                                        meditation.estimated_seconds
                                                    )}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )
                        ) : !ownTracks || ownTracks.length === 0 ? (
                            <p className="text-sm text-room-ink-muted">
                                {t("meditation.meeting.emptyTracks")}
                            </p>
                        ) : (
                            <ul className="space-y-1.5">
                                {ownTracks.map((track) => (
                                    <li key={track.id}>
                                        <button
                                            type="button"
                                            onClick={() => void chooseTrack(track)}
                                            className="flex w-full min-h-11 items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm text-room-ink hover:bg-room-active"
                                        >
                                            <span className="flex min-w-0 items-center gap-2">
                                                <Music
                                                    size={14}
                                                    className="shrink-0 text-room-ink-muted"
                                                    aria-hidden="true"
                                                />
                                                <span className="truncate font-medium">
                                                    {track.name}
                                                </span>
                                            </span>
                                            {track.duration_seconds ? (
                                                <span className="shrink-0 tabular-nums text-xs text-room-ink-muted">
                                                    {formatDuration(
                                                        track.duration_seconds
                                                    )}
                                                </span>
                                            ) : null}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {failed && (
                            <p role="alert" className="mt-3 text-sm text-room-live">
                                {t("meditation.meeting.loadFailed")}
                            </p>
                        )}
                        {recordingMissing && (
                            <p role="alert" className="mt-3 text-sm text-room-live">
                                {t("meditation.audio.missing")}
                            </p>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => (stage.playing ? pause() : void play())}
                            className="room-control"
                            aria-label={t(playLabel)}
                        >
                            {stage.playing ? <Pause size={16} /> : <Play size={16} />}
                            <span>{t(playLabel)}</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => void end()}
                            className="room-control"
                            aria-label={t("meditation.meeting.end")}
                        >
                            <Square size={16} />
                            <span>{t("meditation.meeting.end")}</span>
                        </button>

                        {readAloud && (
                            <label className="flex items-center gap-2 text-xs text-room-ink-muted">
                                <span className="sr-only">
                                    {t("meditation.backgroundTrack")}
                                </span>
                                <select
                                    value={liveBed}
                                    onChange={(e) => void changeBed(e.target.value)}
                                    aria-label={t("meditation.backgroundTrack")}
                                    className="h-9 max-w-[12rem] rounded-lg border border-room-line bg-room-raised px-2 text-xs text-room-ink"
                                >
                                    {BACKGROUND_TRACKS.map((track) => (
                                        <option key={track.id} value={track.id}>
                                            {t(track.labelKey)}
                                        </option>
                                    ))}
                                    {(ownTracks ?? []).map((track) => (
                                        <option key={track.id} value={track.id}>
                                            {track.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        <span className="ml-auto tabular-nums text-xs text-room-ink-muted">
                            {formatDuration(stage.position)}
                            {duration > 0 && ` / ${formatDuration(duration)}`}
                        </span>
                    </div>

                    {stage.error && (
                        <p role="alert" className="text-sm text-room-live">
                            {t(`meditation.meeting.errors.${stage.error}`)}
                        </p>
                    )}

                    {selected.kind === MeetingSelectionKind.MEDITATION &&
                    selected.meditation.script ? (
                        <MeditationPrompter
                            script={selected.meditation.script}
                            wordsPerMinute={selected.meditation.reading_speed_wpm}
                            elapsedSeconds={stage.position}
                            onSeekToSegment={seek}
                            // Only while it is playing: a therapist scrubbing
                            // a paused script should not set bowls off in the
                            // room.
                            onGong={stage.playing ? onGong : undefined}
                        />
                    ) : (
                        <p className="text-sm text-room-ink-muted">
                            {t(
                                selected.kind === MeetingSelectionKind.TRACK
                                    ? "meditation.meeting.trackOnly"
                                    : "meditation.meeting.audioOnly"
                            )}
                        </p>
                    )}
                </>
            )}
        </section>
    );
}

/**
 * What the patient sees: that one has begun, and then their own screen back.
 *
 * No clock and no bar. A meditation is not a thing to wait out, and a bar
 * filling in the corner of the eye is an invitation to measure how much is
 * left — which is the opposite of what the next fifteen minutes are for. The
 * therapist keeps the clock, because the therapist is the one steering.
 *
 * The card itself goes after a few seconds for the same reason. It is there to
 * say "this is starting, close your eyes", and a notice that stays is a screen
 * to keep looking at.
 */
function PatientMeditation({
    channel,
    label,
}: {
    channel: MeditationChannel;
    label: string;
}) {
    const { t } = useTranslation();
    const { state } = channel;
    const [announcing, setAnnouncing] = useState(false);

    // Shown again whenever a different meditation starts, and not for a
    // position packet of the one already under way.
    const meditationId = state?.meditationId ?? null;
    useEffect(() => {
        if (!meditationId) return;
        setAnnouncing(true);
        const timer = setTimeout(() => setAnnouncing(false), PATIENT_NOTICE_MS);
        return () => clearTimeout(timer);
    }, [meditationId]);

    if (!state || !announcing) return null;

    return (
        <section
            className="flex w-full flex-col items-center gap-4 rounded-2xl border border-room-line bg-room-raised px-6 py-10 text-center"
            aria-label={label}
        >
            <MeditationIcon size={32} className="text-room-accent-ink" />
            <h2 className="text-lg font-semibold text-room-ink">{state.title}</h2>
            <p className="text-sm text-room-ink-muted">
                {t("meditation.meeting.patientStarting")}
            </p>
        </section>
    );
}
